import type { ReactNode } from 'react';
import { APP_COPYRIGHT, APP_NAME, APP_VERSION } from '@kontor/shared';
import { WEBSITE } from '../links';
import './auth.css';

const FEATURES = [
  { title: 'Schnell', text: 'Effizient arbeiten', path: 'M13 2 4 14h7l-1 8 9-12h-7z' },
  { title: 'Sicher', text: 'Ihre Daten geschützt', path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4' },
  { title: 'Übersichtlich', text: 'Alles auf einen Blick', path: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8' },
  { title: 'Flexibel', text: 'Immer und überall', path: 'M4 20h16M7 16V9M12 16V5M17 16v-4' },
];

/**
 * Linke Seite der Anmeldung, des Passwort-Vergessens und des Passwort-Festlegens: Bild eines Büros am Abend (public/login/hintergrund.webp: login_szene.webp von Artur, auf
 * die Tasse ist das Wirkia-Zeichen gesetzt; die Texte stehen hier als echter Text), Marke, Überschrift und vier Eckpunkte. Dazu die goldene Trennlinie und die Bögen rechts. Die Seiten selbst liefern nur das Formular rechts.
 * Die Anmeldung ist immer dunkel, unabhängig vom gewählten Design, weil Bild und Farben darauf abgestimmt sind (Variablen in auth.css).
 */
export function AuthBrand({ title, text }: { title: ReactNode; text: string }) {
  return (
    <>
      <aside className="login__brand">
        <img className="login__scene" src="/login/hintergrund.webp" alt="" aria-hidden="true" width="1454" height="720" decoding="async" />
        <a className="brand login__logo" href={WEBSITE} aria-label="Wirkia, zur Startseite von wirkia.de">
          <img className="brand__mark" src="/brand/wirkia-symbol.webp" alt="" width="60" height="26" />
          <span>Wirkia</span>
        </a>
        <div className="login__claim">
          <h1>{title}</h1>
          <p>{text}</p>
          <ul className="login__features">
            {FEATURES.map((f) => (
              <li key={f.title}>
                <span className="login__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={f.path} /></svg>
                </span>
                <strong>{f.title}</strong>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </aside>
      {/* Hinweis und Credits: am Desktop unten links über der Szene, schmal unter dem Formular. Version und Jahr kommen aus dem Code, ein Release braucht hier keine Änderung. */}
      <footer className="login__footer">
        <p>Anmeldungen und Änderungen werden protokolliert.</p>
        <p>© {new Date().getFullYear()} {APP_COPYRIGHT} · {APP_NAME} Version {APP_VERSION}</p>
      </footer>
      {/* Goldene Linie entlang der schrägen Kante der linken Seite */}
      <svg className="login__seam" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <line x1="100" y1="0" x2="86" y2="100" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Bögen rechts oben und unten */}
      <svg className="login__decor" viewBox="0 0 800 1000" preserveAspectRatio="xMaxYMid slice" aria-hidden="true">
        <path d="M800 -40 C 640 -20 520 60 470 0" />
        <path className="login__decor-fill" d="M800 0 L800 230 C 700 230 560 160 520 0 Z" />
        <path d="M800 210 C 690 205 560 140 500 -10" />
        <path className="login__decor-fill" d="M800 1000 L800 760 C 690 760 540 860 470 1000 Z" />
        <path d="M800 650 C 650 680 520 820 430 1010" />
      </svg>
    </>
  );
}

/** Kurze Begrüßung über dem Formular, mit goldenem Strich davor */
export function AuthIntro({ eyebrow, text }: { eyebrow: string; text: string }) {
  return (
    <div className="login__intro">
      <p className="login__eyebrow">{eyebrow}</p>
      <p className="login__lead">{text}</p>
    </div>
  );
}
